import React from "react";
import PropTypes from "prop-types";
import { useForm } from "react-hook-form";
import { Oval } from "react-loader-spinner";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import Select from "components/select";
import { useLookupsContext } from "hooks/useContext";
import { PREFERRED_CURRENCY_USD_ID } from "utils/constantValues";

function EditPreferredCurrency({
  onDiscard = () => {},
  onSave = () => {},
  isLoading = false,
}) {
  const [t] = useTranslation();
  const userDetails = useSelector((state) => state.userDetails || {});

  const { currencies = [] } = useLookupsContext(false);

  const currenciesOptions = currencies.map((elem) => ({
    ...elem,
    value: elem.id,
    label: elem.code,
  }));

  const defaultsValues = {
    preferredCurrency:
      userDetails?.currency?.id ?? PREFERRED_CURRENCY_USD_ID,
  };

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm({ mode: "onChange", defaultValues: defaultsValues });

  return (
    <form
      className="flex flex-col self-stretch rounded-[16px] border-[1px] border-solid border-primary"
      onSubmit={handleSubmit(onSave)}
    >
      <div className="flex p-[24px] justify-between align-center self-stretch border-b border-solid border-[#E4E4E7]">
        <div className="text-[#1D1B22] font-semibold text-[24px]">
          {t("preferredCurrency")}
        </div>
        <div className="flex items-center gap-[12px]">
          <button
            type="button"
            onClick={onDiscard}
            disabled={isLoading}
            className="cursor-pointer flex py-[14px] px-[16px] gap-[8px] justify-center items-center rounded-[6px] border-[1px] border-solid border-default-color bg-white h-[46px]"
          >
            <div className="text-base-black font-bold text-[16px] not-italic">
              {t("Cancel")}
            </div>
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="cursor-pointer flex py-[14px] px-[16px] gap-[8px] justify-center items-center rounded-[6px] border-[1px] border-solid border-primary bg-primary h-[46px]"
          >
            <Oval
              height={30}
              width={30}
              color="#FFF"
              visible={isLoading}
              ariaLabel="oval-loading"
              secondaryColor="#FFF"
              strokeWidth={5}
              strokeWidthSecondary={3}
            />
            <div className="text-white font-bold text-[16px] not-italic">
              {t("saveChangesCta")}
            </div>
          </button>
        </div>
      </div>

      <div className="grid gap-[30px] w-full p-[24px]">
        <Select
          label={t("preferredCurrency")}
          placeholder={t("placeholderSelect", {
            field: t("preferredCurrency").toLowerCase(),
          })}
          name={"preferredCurrency"}
          containerClassName="flex flex-1 flex-col gap-[10px] min-w-[250px] sm-max:w-full"
          value={
            watch("preferredCurrency")
              ? currenciesOptions.find(
                  (elem) => elem.value === watch("preferredCurrency")
                )
              : currenciesOptions.find(
                  (elem) => elem.value === PREFERRED_CURRENCY_USD_ID
                ) ?? null
          }
          onChange={(e) => setValue("preferredCurrency", e.value)}
          isRequired={true}
          errorMessage={errors?.preferredCurrency?.message}
          register={register}
          registerArgs={{
            required: t("errorField", { field: t("preferredCurrency") }),
          }}
          options={currenciesOptions}
        />
      </div>
    </form>
  );
}

EditPreferredCurrency.propTypes = {
  onDiscard: PropTypes.func,
  onSave: PropTypes.func,
  isLoading: PropTypes.bool,
};

export default EditPreferredCurrency;
